import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import { Clock, User } from "iconsax-react";
import ReusableRightDrawer from "./ReusableRightDrawer";
import InfoActionIcons from "./InfoActionIcons";
import {
  dedupeHiringManagerRows,
  fetchHiringManagers,
  isHiringManagerContactRow,
  normalizeHiringManagerEntityType,
} from "../utils/hiringManagers";
import { normalizeContactRoleLabel } from "../utils/contactRoles";
import { areSamePersonNames } from "../utils/recruiterOwnership";
import {
  pillButtonFilledSx,
  pillButtonInteractiveSx,
  withPillButtonClassName,
} from "../utils/pillButtonStyles";
import { formatLocalDate, parseDateTimeValue } from "../utils/dateTime";
import { formatRelativeTimeShort } from "../utils/relativeTime";

const API_BASE = String(process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const BLUE = "#3451F4";
const PURPLE = "#9C4FFF";
const BORDER = "#E7ECF2";
const MUTED = "#98A2B3";
const TEXT = "#344054";
const EMPTY_VALUE = "\u2014";
const STAGE_LABELS = ["New", "Contacted", "Discovery", "Proposal", "Active", "Do Not Contact"];

function cleanText(value, fallback = "") {
  if (value === undefined || value === null) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
}

function formatCreatedShort(value) {
  if (!value) return EMPTY_VALUE;
  const date = parseDateTimeValue(value);
  if (Number.isNaN(date.getTime())) return EMPTY_VALUE;

  return formatLocalDate(date, {
    day: "numeric",
    month: "short",
    weekday: "short",
  });
}

function formatActivityLabel(value) {
  return formatRelativeTimeShort(value, { emptyValue: EMPTY_VALUE });
}

function buildInitials(value, fallback = "HM") {
  const initials = cleanText(value)
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.replace(/[^A-Za-z0-9]/g, "").charAt(0).toUpperCase())
    .filter(Boolean)
    .join("");

  return initials || fallback;
}

function sameStage(left, right) {
  return cleanText(left).toLowerCase() === cleanText(right).toLowerCase();
}

function FieldRow({ label, value, link }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "64px 1fr",
        columnGap: 1,
        py: 0.45,
        alignItems: "start",
      }}
    >
      <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: BLUE }}>
        {label}
      </Typography>

      {link ? (
        <Typography
          component="a"
          href={link}
          target="_blank"
          rel="noreferrer"
          sx={{
            fontSize: 10.5,
            color: PURPLE,
            textDecoration: "none",
            wordBreak: "break-word",
          }}
        >
          {value || EMPTY_VALUE}
        </Typography>
      ) : (
        <Typography
          sx={{
            fontSize: 10.5,
            color: TEXT,
            wordBreak: "break-word",
          }}
        >
          {value || EMPTY_VALUE}
        </Typography>
      )}
    </Box>
  );
}

function TeamMemberActions({ member }) {
  const actions = [
    { type: "phone", href: member?.phone },
    { type: "email", href: member?.email },
    { type: "linkedin", href: member?.linkedin_url },
  ];

  const hasActions = actions.some((action) => cleanText(action.href));
  if (!hasActions) {
    return (
      <Typography sx={{ fontSize: 10.5, color: MUTED }}>
        {EMPTY_VALUE}
      </Typography>
    );
  }

  return (
    <InfoActionIcons
      actions={actions}
      showDisabled={false}
      showGroupDivider={false}
      preserveOrder={true}
      gap={0.55}
      iconSize={16}
    />
  );
}

export default function HiringManagerProfileDrawer({
  open,
  onClose,
  manager,
  onShowMore,
}) {
  const [entityProfile, setEntityProfile] = useState(null);
  const [entityLoading, setEntityLoading] = useState(false);
  const [entityError, setEntityError] = useState("");
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);

  const entityType = normalizeHiringManagerEntityType(manager?.entity_type);
  const entityId = Number(manager?.entity_id || 0);

  useEffect(() => {
    if (!open || !entityType || !entityId) {
      if (!open) {
        setEntityProfile(null);
        setEntityLoading(false);
        setEntityError("");
      }
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setEntityLoading(true);
    setEntityError("");

    const url =
      entityType === "lead"
        ? `${API_BASE}/api/leads/${encodeURIComponent(String(entityId))}`
        : `${API_BASE}/api/companies/${encodeURIComponent(String(entityId))}`;

    fetch(url, {
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          const message = await response.text().catch(() => "");
          throw new Error(message || `Failed to load entity profile (${response.status})`);
        }

        return response.json().catch(() => ({}));
      })
      .then((payload) => {
        if (!active) return;
        setEntityProfile(payload?.lead || payload?.company || payload || null);
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to load hiring manager entity profile:", error);
        setEntityError("Unable to load the latest related profile data.");
      })
      .finally(() => {
        if (active) setEntityLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [entityId, entityType, open]);

  useEffect(() => {
    if (!open || !entityType || !entityId) {
      if (!open) {
        setTeamMembers([]);
        setTeamLoading(false);
      }
      return undefined;
    }

    const controller = new AbortController();
    let active = true;

    setTeamMembers([]);
    setTeamLoading(true);

    fetchHiringManagers({
      apiBase: API_BASE,
      entityType,
      entityId,
      signal: controller.signal,
    })
      .then((rows) => {
        if (!active) return;
        setTeamMembers(Array.isArray(rows) ? rows : []);
      })
      .catch((error) => {
        if (!active || error?.name === "AbortError") return;
        console.error("Failed to load hiring manager team members:", error);
      })
      .finally(() => {
        if (active) setTeamLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [entityId, entityType, open]);

  const mergedManager = useMemo(() => {
    const matchedTeamMember = (teamMembers || []).find((member) => {
      if (manager?.id && member?.id && String(manager.id) === String(member.id)) return true;

      const managerEmail = cleanText(manager?.email).toLowerCase();
      const memberEmail = cleanText(member?.email).toLowerCase();
      if (managerEmail && memberEmail && managerEmail === memberEmail) return true;

      return areSamePersonNames(manager?.name, member?.name);
    });

    return {
      ...(manager || {}),
      ...(matchedTeamMember || {}),
    };
  }, [manager, teamMembers]);

  const currentStatus = cleanText(
    entityProfile?.status || manager?.status || "No stage",
    "No stage"
  );
  const companyName = cleanText(
    manager?.company ||
      manager?.entity_name ||
      entityProfile?.company_name ||
      entityProfile?.name,
    EMPTY_VALUE
  );
  const managerName = cleanText(mergedManager?.name, "Hiring Manager");
  const managerRole = normalizeContactRoleLabel(
    mergedManager?.role || mergedManager?.title || manager?.role,
    {
      contactKind: mergedManager?.contact_kind || manager?.contact_kind,
      source: mergedManager?.source || manager?.source,
      fallback: "Hiring Manager",
    }
  );
  const phone = cleanText(mergedManager?.phone);
  const email = cleanText(mergedManager?.email);
  const linkedin = cleanText(mergedManager?.linkedin_url);
  const website = cleanText(
    manager?.website_url ||
      entityProfile?.website ||
      entityProfile?.company_website ||
      entityProfile?.website_url
  );
  const source = cleanText(
    mergedManager?.source || manager?.source || entityProfile?.source,
    EMPTY_VALUE
  );
  const recruiterName = cleanText(
    manager?.owner ||
      entityProfile?.owner_name ||
      entityProfile?.owner ||
      entityProfile?.team_member,
    EMPTY_VALUE
  );
  const createdLabel = formatCreatedShort(
    manager?.created_at || entityProfile?.created_at || entityProfile?.createdAt
  );
  const activityLabel = formatActivityLabel(
    entityProfile?.last_activity_time ||
      entityProfile?.updated_at ||
      manager?.updated_at ||
      manager?.created_at
  );
  const activityTitle = cleanText(
    entityProfile?.last_activity_type || currentStatus,
    "No recent activity recorded"
  );
  const activityMessage = cleanText(
    entityProfile?.last_activity_message ||
      entityProfile?.last_activity_detail ||
      entityProfile?.notes ||
      manager?.last_activity ||
      "",
    "No recent activity details available yet."
  );

  const derivedTeamMembers = useMemo(() => {
    const rows = dedupeHiringManagerRows([
      mergedManager,
      ...teamMembers,
      recruiterName && recruiterName !== EMPTY_VALUE
        ? {
            name: recruiterName,
            role: "Recruiter",
            contact_kind: "recruiter",
          }
        : null,
    ].filter(Boolean));

    return rows
      .filter((row) => cleanText(row?.name))
      .map((row) => ({
        ...row,
        role: normalizeContactRoleLabel(row?.role || row?.title, {
          contactKind: row?.contact_kind,
          source: row?.source,
          fallback: isHiringManagerContactRow(row) ? "Hiring Manager" : "Team Member",
        }),
      }));
  }, [mergedManager, recruiterName, teamMembers]);

  const visibleTeamMembers = useMemo(() => derivedTeamMembers.slice(0, 5), [derivedTeamMembers]);

  const headerLeft = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.9, minWidth: 0 }}>
      <Box
        sx={{
          width: 16,
          height: 16,
          borderRadius: "999px",
          border: "1.5px solid #7C3AED",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "0 0 auto",
        }}
      >
        <User size="10" color="#7C3AED" variant="Outline" />
      </Box>

      <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: BLUE }} noWrap>
        Hiring Manager Profile
      </Typography>
    </Box>
  );

  if (!open) return null;

  return (
    <ReusableRightDrawer
      open={open}
      onClose={onClose}
      id="hiring-manager-profile-drawer"
      width={320}
      headerLeft={headerLeft}
      closeColor={PURPLE}
      paperSx={{
        borderRadius: "14px 0 0 14px",
        boxShadow: "0 18px 45px rgba(2, 6, 23, 0.22)",
      }}
      footer={
        <Button
          variant="contained"
          size="small"
          className={withPillButtonClassName()}
          disabled={!manager || typeof onShowMore !== "function"}
          onClick={() => onShowMore?.(manager)}
          sx={[
            pillButtonInteractiveSx,
            pillButtonFilledSx,
            {
              display: "block",
              mx: "auto",
              minWidth: 96,
              height: 22,
              fontSize: 9.5,
              fontWeight: 400,
              boxShadow: "none",
            },
          ]}
        >
          Show more
        </Button>
      }
    >
      {!manager ? (
        <Alert severity="info">No hiring manager selected.</Alert>
      ) : (
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.1, mb: 1.8 }}>
            <Avatar
              src={cleanText(mergedManager?.profile_image) || undefined}
              sx={{
                width: 38,
                height: 38,
                fontSize: 11,
                fontWeight: 700,
                background: cleanText(mergedManager?.profile_image)
                  ? "transparent"
                  : "linear-gradient(135deg,#F4EBFF,#FCE7F3)",
                color: "#7F56D9",
              }}
            >
              {buildInitials(managerName)}
            </Avatar>

            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: TEXT }} noWrap>
                {managerName}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: "#667085", lineHeight: 1.2 }} noWrap>
                {managerRole}
              </Typography>
              <Typography sx={{ fontSize: 10, color: MUTED, lineHeight: 1.2 }} noWrap>
                {companyName}
              </Typography>
            </Box>
          </Box>

          <Typography sx={{ fontSize: 16, fontWeight: 700, color: BLUE, lineHeight: 1.2, mb: 0.9 }}>
            Quick Actions:
          </Typography>

          <Box sx={{ mb: 1.8 }}>
            <InfoActionIcons
              showDisabled={true}
              showGroupDivider={false}
              preserveOrder={true}
              gap={0.95}
              iconSize={16}
              variant="filled"
              filledWidth={34}
              filledHeight={22}
              actions={[
                { type: "phone", href: phone },
                { type: "email", href: email },
                { type: "linkedin", href: linkedin },
                { type: "website", href: website },
              ]}
            />
          </Box>

          <Divider sx={{ borderColor: BORDER, mb: 1.6 }} />

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.85 }}>
            Stages:
          </Typography>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.85, mb: 0.85 }}>
            {STAGE_LABELS.map((stage) => (
              <Typography
                key={stage}
                sx={{
                  fontSize: 9.5,
                  fontWeight: sameStage(stage, currentStatus) ? 700 : 500,
                  color: sameStage(stage, currentStatus) ? PURPLE : "#C084FC",
                }}
              >
                {stage}
              </Typography>
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
            <Typography
              sx={{
                fontSize: 10,
                color: PURPLE,
                fontWeight: 600,
                cursor: typeof onShowMore === "function" ? "pointer" : "default",
              }}
              onClick={() => onShowMore?.(manager)}
            >
              View all
            </Typography>
          </Box>

          <Divider sx={{ borderColor: BORDER, mb: 1.6 }} />

          <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE, mb: 0.9 }}>
            Last Activity:
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.55, mb: 0.7 }}>
            <Clock size="14" color={MUTED} variant="Linear" />
            <Typography sx={{ fontSize: 10, color: MUTED }}>
              {activityLabel}
            </Typography>
          </Box>

          <Typography sx={{ fontSize: 10.5, color: TEXT, lineHeight: 1.45, mb: 0.35 }}>
            {activityTitle}
          </Typography>
          <Typography sx={{ fontSize: 10.5, color: "#667085", lineHeight: 1.45 }}>
            {activityMessage}
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 0.65, mb: 1.55 }}>
            <Typography
              sx={{
                fontSize: 10,
                color: PURPLE,
                fontWeight: 600,
                cursor: typeof onShowMore === "function" ? "pointer" : "default",
              }}
              onClick={() => onShowMore?.(manager)}
            >
              View
            </Typography>
          </Box>

          <Divider sx={{ borderColor: BORDER, mb: 1.2 }} />

          <FieldRow label="Status" value={currentStatus} />
          <FieldRow label="Phone" value={phone || EMPTY_VALUE} link={phone ? `tel:${phone}` : undefined} />
          <FieldRow label="Email" value={email || EMPTY_VALUE} link={email ? `mailto:${email}` : undefined} />
          <FieldRow label="LinkedIn" value={linkedin || EMPTY_VALUE} link={linkedin || undefined} />
          <FieldRow label="Source" value={source} />

          <Divider sx={{ borderColor: BORDER, my: 1.5 }} />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 1,
              alignItems: "start",
              mb: 1,
            }}
          >
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLUE }}>
              Hiring Team
            </Typography>

            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: 9.5, color: "#475467" }}>
                Recruiter:{" "}
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {recruiterName}
                </Box>
              </Typography>
              <Typography sx={{ fontSize: 9, color: MUTED }}>
                Created: {createdLabel}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1.2fr 1fr auto",
              columnGap: 1,
              pb: 0.65,
              borderBottom: `1px solid ${BORDER}`,
              mb: 0.2,
            }}
          >
            <Typography sx={{ fontSize: 9.5, color: MUTED }}>Name</Typography>
            <Typography sx={{ fontSize: 9.5, color: MUTED }}>Role</Typography>
            <Typography sx={{ fontSize: 9.5, color: MUTED, textAlign: "right" }}>
              Actions
            </Typography>
          </Box>

          {teamLoading ? (
            <Typography sx={{ fontSize: 10, color: MUTED, py: 1 }}>
              Loading hiring team...
            </Typography>
          ) : visibleTeamMembers.length ? (
            visibleTeamMembers.map((member, index) => (
              <Box
                key={`${member.id ?? member.name}-${index}`}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1fr auto",
                  columnGap: 1,
                  alignItems: "center",
                  py: 0.85,
                  borderBottom:
                    index === visibleTeamMembers.length - 1
                      ? "none"
                      : `1px solid ${BORDER}`,
                }}
              >
                <Typography sx={{ fontSize: 10.5, color: TEXT }} noWrap>
                  {member.name}
                </Typography>
                <Typography sx={{ fontSize: 10, color: "#667085" }} noWrap>
                  {member.role}
                </Typography>
                <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <TeamMemberActions member={member} />
                </Box>
              </Box>
            ))
          ) : (
            <Typography sx={{ fontSize: 10, color: MUTED, py: 1 }}>
              No hiring team members available yet.
            </Typography>
          )}

          <Box sx={{ mt: 1.15 }}>
            <Typography sx={{ fontSize: 9.5, color: MUTED, mb: 0.5 }}>
              Source
            </Typography>
            <Box
              sx={{
                minHeight: 32,
                border: "1px solid #D0D5DD",
                borderRadius: "6px",
                px: 1.2,
                display: "flex",
                alignItems: "center",
                fontSize: 10.5,
                color: "#475467",
                backgroundColor: "#fff",
              }}
            >
              {source}
            </Box>
          </Box>

          {(entityLoading || entityError) ? (
            <Stack direction="row" spacing={0.8} alignItems="center" sx={{ mt: 1.25 }}>
              {entityLoading ? <CircularProgress size={13} /> : null}
              <Typography sx={{ fontSize: 9.5, color: MUTED }}>
                {entityLoading ? "Refreshing profile..." : entityError}
              </Typography>
            </Stack>
          ) : null}
        </Box>
      )}
    </ReusableRightDrawer>
  );
}
